import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useReducer,
} from 'react';
import { v4 as uuid } from 'uuid';
import { AddedItem } from '../types/items.types';

export const AddItemsContext = createContext<AddedItem[]>([]);
export const AddItemsDispatchContext = createContext<Dispatch<ItemsActions>>(
  () => {},
);

/**
 * Retrieves the current value of the AddItemsContext.
 * This hook provides access to the context value for managing items globally.
 */
export function useAddItems() {
  return useContext(AddItemsContext);
}

/**
 * A custom hook to access the dispatch function for the items context.
 *
 * This hook retrieves the dispatch function from the AddItemsDispatchContext,
 * enabling components to trigger actions that update the state of the items.
 */
export function useAddItemsDispatch() {
  return useContext(AddItemsDispatchContext);
}

export interface AddedItemAction {
  type: 'ADDED';
}

export interface RemovedItemAction {
  type: 'REMOVED';
  payload: { id: string };
}

export interface ChangedItemAction {
  type: 'CHANGED';
  payload: { id: string; updates: Partial<Omit<AddedItem, 'id'>> };
}

export interface ResetItemsAction {
  type: 'RESET';
}

/**
 * Represents the actions that can be performed on a collection of items.
 */
export type ItemsActions =
  | AddedItemAction
  | RemovedItemAction
  | ChangedItemAction
  | ResetItemsAction;

/**
 * Reducer function to manage an array of item state based on different action types.
 *
 * @param {AddedItem[]} items - The current array of items in the state.
 * @param {ItemsActions} action - The action object containing the type and payload to modify the state.
 */
function itemsReducer(items: AddedItem[], action: ItemsActions) {
  switch (action.type) {
    case 'ADDED':
      return [
        ...items,
        {
          id: uuid(),
          jr_cost: 0,
          plus_cost: null,
          lead_time_in_weeks: 0,
          fashionnova_exclusive: false,
          fit_sample: false,
          buyer_pick: false,
        },
      ];
    case 'REMOVED':
      return items.filter((item) => item.id !== action.payload.id);
    case 'CHANGED':
      return items.map((item) =>
        item.id === action.payload.id
          ? {
              ...item,
              ...action.payload.updates,
            }
          : item,
      );
    case 'RESET':
      return [
        {
          id: uuid(),
          jr_cost: 0,
          plus_cost: null,
          lead_time_in_weeks: 0,
          fashionnova_exclusive: false,
          fit_sample: false,
          buyer_pick: false,
        },
      ];
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
}

/**
 * Provides context for managing a collection of items and their state.
 * It allows components to consume the current state of items and to trigger updates
 * via a dispatch function.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components that will consume the context.
 * @param {AddedItem} [props.initialItem] - The initial item to populate the list of items, if provided.
 */
export function AddItemsProvider({
  children,
  initialItem,
}: PropsWithChildren & { initialItem?: AddedItem }) {
  const [items, dispatch] = useReducer(
    itemsReducer,
    initialItem ? [initialItem] : [],
  );

  return (
    <AddItemsContext.Provider value={items}>
      <AddItemsDispatchContext.Provider value={dispatch}>
        {children}
      </AddItemsDispatchContext.Provider>
    </AddItemsContext.Provider>
  );
}
